// Mock data for Agriculture Farm Leasing Website - ApnaFarm

export const heroData = {
  headline1: "We Farm.",
  headline2: "You Get Paid.",
  subheadline: "Managed organic farmland on lease with active plantation in West Bengal",
  trustBadges: [
    { icon: "Users", text: "1000+ Acres Under Management" },
    { icon: "Leaf", text: "Premium Organic Cultivation" },
    { icon: "CreditCard", text: "₹10,000 per Cottah" },
    { icon: "Calendar", text: "Minimum 2.5X in 30 Months" }
  ]
};

export const howItWorksSteps = [
  {
    step: 1,
    title: "Choose Your Land",
    description: "Select your preferred lease area starting from just 1 cottah at ₹10,000 per cottah.",
    icon: "MapPin"
  },
  {
    step: 2,
    title: "We Plant & Manage",
    description: "Our expert team handles plantation, irrigation, and all farm operations on your behalf.",
    icon: "Sprout"
  },
  {
    step: 3,
    title: "Harvest & Sell",
    description: "Premium fruits are harvested and sold to established market channels.",
    icon: "Package"
  },
  {
    step: 4,
    title: "You Get Paid",
    description: "Receive income based on produce sales - choose quarterly payouts or lump-sum settlement.",
    icon: "Wallet"
  }
];

export const incomeData = {
  leasePrice: "₹10,000",
  perUnit: "per Cottah",
  exampleLease: "₹1,00,000",
  exampleArea: "10 cottah",
  maxTenure: "2.5 Years",
  minArea: "1 Cottah",

  plan36: {
    title: "30 Month Lease",
    subtitle: "Minimum 2X Returns • Phased Income",
    totalPayout: "₹2,00,000",
    schedule: [
      { period: "1–3 months", amount: "₹10,000" },
      { period: "4–6 months", amount: "₹10,000" },
      { period: "7–9 months", amount: "₹15,000" },
      { period: "10–12 months", amount: "₹15,000" },
      { period: "13–15 months", amount: "₹25,000" },
      { period: "16–18 months", amount: "₹25,000" },
      { period: "19–21 months", amount: "₹25,000" },
      { period: "22–24 months", amount: "₹25,000" },
      { period: "25–27 months", amount: "₹25,000" },
      { period: "28–30 months", amount: "₹25,000" }
    ]
  },

  plan42: {
    title: "30 Month Lease",
    subtitle: "Minimum 2.5X Returns • One-Time Settlement",
    multiplier: "2.5X",
    duration: "in 30 Months",
    amount: "₹2,50,000",
    note: "One-time lump-sum income at the end of lease period"
  },

  disclaimer: "All income figures are based on minimum estimates; they may be higher depending on production. Returns are based on conservative estimates of projected farm yield and produce sales. Terms and conditions apply."
};

export const cropsData = [
  {
    name: "Jackfruit",
    image: "/images/jackfruit.jpg",
    description: "High-yield tropical fruit with strong domestic and export demand."
  },
  {
    name: "Golden Guava",
    image: "/images/guava.jpg",
    description: "Popular commercial fruit with consistent market demand."
  },
  {
    name: "Katimon Mango",
    image: "/images/mango.jpg",
    description: "Premium mango variety grown for export and domestic markets."
  },
  {
    name: "Kumquat Lemon",
    image: "/images/lemon.webp",
    description: "Fast-growing citrus with high repeat yield potential."
  }
];

export const faqData = [
  {
    question: "How does the lease program work?",
    answer: "You lease farmland with a one-time payment starting at ₹10,000 per cottah. ApnaFarm manages all farming operations - plantation, maintenance, harvesting, and sales. You receive income based on farm produce sales."
  },
  {
    question: "What are the payout options?",
    answer: "Choose between two 30-month lease plans: phased income aligned with cultivation cycles (minimum 2X - total ₹2,00,000 on ₹1 lakh lease), or a one-time settlement at the end of the lease (minimum 2.5X - ₹2,50,000 on ₹1 lakh lease). Plans must be selected at the start and cannot be changed."
  },
  {
    question: "Are the returns guaranteed?",
    answer: "No. Returns are based on conservative estimates of projected farm yield and produce sales - not a fixed or guaranteed product. The figures shown are minimum estimates and may be higher depending on actual production. Income follows natural cultivation and harvest cycles."
  },
  {
    question: "What happens if a crop underperforms?",
    answer: "Payouts are tied to actual harvest and produce sales. We diversify across multiple high-demand fruit varieties and use scientific farming methods to reduce risk, but as with any agricultural venture, output can vary with natural conditions."
  },
  {
    question: "Can I visit the farmland?",
    answer: "Yes. Once you begin your lease, you are welcome to visit the plantation and see your leased area and the ongoing operations first-hand. Our team can arrange a guided farm visit."
  },
  {
    question: "What happens at the end of the 30-month term?",
    answer: "Your selected plan determines how you are paid out - either phased income across the cultivation cycles, or a single lump-sum settlement at the end of the term. The lease plan, tenure, and income structure are locked in at inception and cannot be changed mid-term."
  },
  {
    question: "What crops are grown?",
    answer: "We grow premium organic fruit crops including Jackfruit, Golden Guava, Katimon Mango, and Kumquat Lemon - selected for high yield and strong market demand."
  },
  {
    question: "Who manages the farms?",
    answer: "ApnaFarm is operated by SG Aqua & Garden Fresh Pvt. Ltd., with nearly two decades of experience in modern agriculture. We employ scientific farming methods and support a rural workforce of over 450 people."
  }
];

// Company credibility / trust signals for the "Why ApnaFarm" section
export const whyApnaFarm = {
  eyebrow: "Why ApnaFarm",
  title: "Farmland you can trust, managed end-to-end",
  description: "ApnaFarm is operated by SG Aqua & Garden Fresh Pvt. Ltd. - nearly two decades of hands-on experience in modern, scientific agriculture across West Bengal.",
  stats: [
    { value: "20", suffix: "Years", label: "In modern agriculture" },
    { value: "1000", suffix: "+ Acres", label: "Under active management" },
    { value: "450", suffix: "+ People", label: "Rural workforce supported" },
    { value: "100", suffix: "% Organic", label: "Premium cultivation" }
  ],
  pillars: [
    {
      icon: "ShieldCheck",
      title: "Locked-in at inception",
      text: "Your lease plan, tenure, and income structure are fixed at the start of the agreement - no surprises, no mid-term changes."
    },
    {
      icon: "Sprout",
      title: "Professionally managed",
      text: "Every stage - plantation, irrigation, maintenance, harvesting, and sales - is handled exclusively by our expert team."
    },
    {
      icon: "TrendingUp",
      title: "Conservative estimates",
      text: "Projected returns are based on conservative farm-yield assumptions, so the figures you see are minimum expectations."
    },
    {
      icon: "Leaf",
      title: "Aligned with nature",
      text: "Income timelines follow real cultivation and harvest cycles - the way sustainable farming actually works."
    }
  ]
};

export const investmentOptions = [
  "1-5 Cottahs (₹10,000 - ₹50,000)",
  "6-10 Cottahs (₹60,000 - ₹1,00,000)",
  "11-20 Cottahs (₹1,10,000 - ₹2,00,000)",
  "21-50 Cottahs (₹2,10,000 - ₹5,00,000)",
  "50+ Cottahs (₹5,00,000+)"
];

export const footerData = {
  tagline: "Growing Prosperity Through Agriculture.",
  email: "enquiry@apnafarm.net",
  phone: "6292050222",
  website: "www.apnafarm.net",
  companyLine: "ApnaFarm™ is a brand owned and operated by SG Aqua & Garden Fresh Pvt. Ltd.",
  registeredAddress: "Sugandha More, Delhi Road, NH2 Bypass, Hooghly, West Bengal - 712102",
  corporateAddress: "Godrej Genesis Building, 2nd Floor, Block EP & GP, Sector-V, Salt Lake Electronic Complex, Kolkata - 700091",
  disclaimerLine1: "Payout figures are indicative and based on conservative estimates of farm harvest and produce sale.",
  disclaimerLine2: "This is not a fixed-return product. Terms and conditions apply.",
  disclaimerLine3: "© 2026 SG Aqua & Garden Fresh Pvt. Ltd. All rights reserved."
};
